import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Session } from '@supabase/supabase-js';

interface SearchFilters {
  type?: string;
  status?: string;
  riskLevel?: string;
  hasDeadline?: boolean;
  minDate?: string;
  maxDate?: string;
  folderId?: string;
}

interface SearchResult {
  id: string;
  title: string;
  type: string;
  status: string;
  risk_level: string | null;
  deadline: string | null;
  created_at: string;
  updated_at: string;
  folder_id: string | null;
  relevance: number;
}

interface SavedSearch {
  id: string;
  name: string;
  query: string;
  filters: SearchFilters;
  search_type: string;
  is_favorite: boolean;
  created_at: string;
}

interface AdvancedSearchProps {
  session: Session;
  onNavigateToCase: (caseId: string) => void;
  onClose: () => void;
}

export const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ session, onNavigateToCase, onClose }) => {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [popularSearches, setPopularSearches] = useState<Array<{ query: string; search_count: number }>>([]);

  useEffect(() => {
    loadSavedSearches();
    loadPopularSearches();
  }, [session.user.id]);

  const loadSavedSearches = async () => {
    try {
      const { data, error } = await supabase
        .from('saved_searches')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedSearches(data || []);
    } catch (err) {
      console.error('Error loading saved searches:', err);
    }
  };

  const loadPopularSearches = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_popular_searches', {
          user_uuid: session.user.id,
          limit_count: 5,
        });

      if (error) throw error;
      setPopularSearches(data || []);
    } catch (err) {
      console.error('Error loading popular searches:', err);
    }
  };

  const handleSearch = useCallback(async () => {
    if (!query.trim() && Object.keys(filters).length === 0) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Convert query to tsquery format (simple word matching)
      const tsQuery = query
        .trim()
        .split(/\s+/)
        .filter(word => word.length > 0)
        .map(word => `${word}:*`)
        .join(' & ');

      const { data, error: searchError } = await supabase
        .rpc('search_cases', {
          user_uuid: session.user.id,
          search_query: tsQuery || '',
          case_type: filters.type || null,
          case_status: filters.status || null,
          risk_level: filters.riskLevel || null,
          has_deadline: filters.hasDeadline ?? null,
          min_date: filters.minDate || null,
          max_date: filters.maxDate || null,
          folder_id_filter: filters.folderId || null,
          limit_results: 50,
          offset_results: 0,
        });

      if (searchError) throw searchError;

      setResults(data || []);

      // Save search to history
      await supabase.rpc('save_search_query', {
        user_uuid: session.user.id,
        search_text: query,
        search_filters: filters,
        search_category: 'cases',
        result_count: (data || []).length,
      });

      loadPopularSearches(); // Refresh popular searches
    } catch (err) {
      console.error('Error performing search:', err);
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [query, filters, session.user.id]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (query.length >= 2 || Object.keys(filters).length > 0) {
        handleSearch();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [query, filters, handleSearch]);

  const handleSaveSearch = async () => {
    if (!saveSearchName.trim()) return;

    try {
      const { error } = await supabase
        .from('saved_searches')
        .insert({
          user_id: session.user.id,
          name: saveSearchName,
          query,
          filters,
          search_type: 'cases',
        });

      if (error) throw error;

      setSaveSearchName('');
      setShowSaveModal(false);
      loadSavedSearches();
    } catch (err) {
      console.error('Error saving search:', err);
      alert(err instanceof Error ? err.message : 'Failed to save search');
    }
  };

  const handleLoadSavedSearch = (saved: SavedSearch) => {
    setQuery(saved.query);
    setFilters(saved.filters);
  };

  const handleDeleteSavedSearch = async (id: string) => {
    try {
      const { error } = await supabase
        .from('saved_searches')
        .delete()
        .eq('id', id);

      if (error) throw error;
      loadSavedSearches();
    } catch (err) {
      console.error('Error deleting saved search:', err);
    }
  };

  const getRiskColor = (risk: string | null) => {
    switch (risk?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
      case 'low':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-luna-secondary text-3xl">search</span>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Erweiterte Suche</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Schließen"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Search Input */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                search
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Suche nach Fällen, Dokumenten, Vorlagen..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-luna-secondary focus:border-transparent"
                autoFocus
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                showFilters || Object.keys(filters).length > 0
                  ? 'bg-luna-secondary text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <span className="material-symbols-outlined">tune</span>
              <span>Filter</span>
              {Object.keys(filters).length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {Object.keys(filters).length}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowSaveModal(true)}
              disabled={!query && Object.keys(filters).length === 0}
              className="px-6 py-3 rounded-lg font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="Suche speichern"
            >
              <span className="material-symbols-outlined">bookmark_add</span>
            </button>
          </div>

          {/* Popular Searches */}
          {popularSearches.length > 0 && !query && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Häufige Suchen:</p>
              <div className="flex flex-wrap gap-2">
                {popularSearches.map((search, idx) => (
                  <button
                    key={idx}
                    onClick={() => setQuery(search.query)}
                    className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {search.query}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Falltyp
                </label>
                <select
                  value={filters.type || ''}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Alle Typen</option>
                  <option value="DSGVO">DSGVO</option>
                  <option value="SCHUFA">SCHUFA</option>
                  <option value="Mietrecht">Mietrecht</option>
                  <option value="Arbeitsrecht">Arbeitsrecht</option>
                  <option value="Verbraucherrecht">Verbraucherrecht</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Alle Status</option>
                  <option value="Active">Aktiv</option>
                  <option value="Pending">Ausstehend</option>
                  <option value="Resolved">Gelöst</option>
                  <option value="Closed">Geschlossen</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Risikostufe
                </label>
                <select
                  value={filters.riskLevel || ''}
                  onChange={(e) => setFilters({ ...filters, riskLevel: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Alle Risiken</option>
                  <option value="High">Hoch</option>
                  <option value="Medium">Mittel</option>
                  <option value="Low">Niedrig</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Von Datum
                </label>
                <input
                  type="date"
                  value={filters.minDate || ''}
                  onChange={(e) => setFilters({ ...filters, minDate: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bis Datum
                </label>
                <input
                  type="date"
                  value={filters.maxDate || ''}
                  onChange={(e) => setFilters({ ...filters, maxDate: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.hasDeadline ?? false}
                    onChange={(e) => setFilters({ ...filters, hasDeadline: e.target.checked || undefined })}
                    className="w-4 h-4 text-luna-secondary border-gray-300 rounded focus:ring-luna-secondary"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Nur mit Frist</span>
                </label>
              </div>

              <div className="md:col-span-3 flex justify-end gap-2">
                <button
                  onClick={() => setFilters({})}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Filter zurücksetzen
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Saved Searches Sidebar */}
        {savedSearches.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gespeicherte Suchen:</p>
            <div className="flex flex-wrap gap-2">
              {savedSearches.slice(0, 5).map((saved) => (
                <div key={saved.id} className="flex items-center gap-1 bg-white dark:bg-gray-700 rounded-lg px-3 py-1.5 border border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => handleLoadSavedSearch(saved)}
                    className="text-sm text-luna-secondary hover:underline"
                  >
                    {saved.name}
                  </button>
                  <button
                    onClick={() => handleDeleteSavedSearch(saved.id)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <span className="material-symbols-outlined text-xs">close</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {!loading && !error && results.length === 0 && (query || Object.keys(filters).length > 0) && (
            <div className="text-center py-12">
              <span className="material-symbols-outlined text-gray-400 dark:text-gray-500 text-6xl mb-4">
                search_off
              </span>
              <p className="text-gray-600 dark:text-gray-400">Keine Ergebnisse gefunden</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                Versuchen Sie, Ihre Suchbegriffe zu ändern oder Filter anzupassen
              </p>
            </div>
          )}

          {!loading && !error && results.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {results.length} {results.length === 1 ? 'Ergebnis' : 'Ergebnisse'} gefunden
              </p>
              {results.map((result) => (
                <div
                  key={result.id}
                  onClick={() => {
                    onNavigateToCase(result.id);
                    onClose();
                  }}
                  className="p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-luna-secondary dark:hover:border-luna-secondary cursor-pointer transition-all hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2 truncate">
                        {result.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded">
                          {result.type}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded">
                          {result.status}
                        </span>
                        {result.risk_level && (
                          <span className={`px-2 py-1 rounded ${getRiskColor(result.risk_level)}`}>
                            {result.risk_level} Risiko
                          </span>
                        )}
                        {result.deadline && (
                          <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <span className="material-symbols-outlined text-xs">event</span>
                            {new Date(result.deadline).toLocaleDateString('de-DE')}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="material-symbols-outlined text-gray-400">arrow_forward</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Search Modal */}
        {showSaveModal && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Suche speichern</h3>
              <input
                type="text"
                value={saveSearchName}
                onChange={(e) => setSaveSearchName(e.target.value)}
                placeholder="Name für die gespeicherte Suche"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-4"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveSearch}
                  disabled={!saveSearchName.trim()}
                  className="px-4 py-2 bg-luna-secondary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
